import { useEffect } from "react";
import { USER_INFO_KEY } from "../constants/common";
import { SSO_PROVIDERS } from "../helpers/crossLoginHelper";
import { isImpersonatedToken } from "../helpers/impersonationHelper";

const parseUserInfo = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isSSOSession = (parsed) => {
  const provider = parsed?.token?.providerLogin;
  return SSO_PROVIDERS.includes(provider);
};

const isImpersonationSession = (parsed) =>
  isImpersonatedToken(parsed?.token?.accessToken);

// Impersonation is detected independently of isSSOSession/SSO_PROVIDERS -
// it's read straight off the access token's impersonatedBy claim (see
// isImpersonatedToken in impersonationHelper.js), not a providerLogin
// value, so it can't be dragged into SSO_PROVIDERS-gated behavior (like
// crossLoginHelper.js's BLS cross-domain handoff) by accident. This tab
// resync check is a different concern from that handoff: USER_INFO_KEY is
// shared localStorage on this origin, so starting or ending an
// impersonated session in one tab silently swaps the identity every other
// tab on this origin is using, unless something forces those tabs to
// catch up.
export const shouldResyncSession = (oldSession, newSession) => {
  const oldUserId = oldSession?.userInfo?.userId;
  const newUserId = newSession?.userInfo?.userId;
  if (oldUserId === newUserId) return false;

  return (
    isSSOSession(oldSession) ||
    isSSOSession(newSession) ||
    isImpersonationSession(oldSession) ||
    isImpersonationSession(newSession)
  );
};

const useSSOCrossTabSync = () => {
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== USER_INFO_KEY) return;

      const oldSession = parseUserInfo(e.oldValue);
      const newSession = parseUserInfo(e.newValue);

      if (shouldResyncSession(oldSession, newSession)) {
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
};

export default useSSOCrossTabSync;
